import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { AUTHORITY } from '../constants/common';
import { GET_ROLES_INFO, GET_WORKSPACE_ROLES } from '../apis/role';
import { GET_WORKSPACE, GET_WORKSPACE_PROFILE } from '../apis/workspace';
import { SUBSCRIPTION_LIMITATIONS, WORKSPACE_SUBSCRIPTION } from '../constants/workspaces';
import { useAuth } from './AuthContext';
import { WORKSPACE_URL_REGEX } from '../constants/routes';
import ROLES from '../constants/roles';
import useLoader from './LoaderContext';

const WorkspaceContext = createContext({});

const workspaceRegex = new RegExp(WORKSPACE_URL_REGEX);

export const WorkspaceProvider = ({ children }) => {
  // init
  const { authUser } = useAuth();
  const { isLoading, setIsLoading } = useLoader();

  // states
  const [handle, setHandle] = useState(undefined);
  const [userPermissions, setUserPermissions] = useState(undefined);
  const [userRoles, setUserRoles] = useState([]);
  const [workspace, setWorkspace] = useState(undefined);
  const [workspaceProfile, setWorkspaceProfile] = useState(undefined);
  const [timelineTour, setTimelineTour] = useState(true);
  const [teamTour, setTeamTour] = useState(true);
  const [epicTour, setEpicTour] = useState(true);
  const [projectTour, setProjectTour] = useState(true);
  const [showUpgradePlan, setShowUpgradePlan] = useState(false);

  // constants
  const isFreeTrial = useMemo(() => (workspace ? workspace.subscription === WORKSPACE_SUBSCRIPTION.TRIAL.description : true), [workspace]);
  const freeUsersLimit = useMemo(() => SUBSCRIPTION_LIMITATIONS[WORKSPACE_SUBSCRIPTION.TRIAL].users, [isFreeTrial]);
  const freeAddProjectsLimit = useMemo(() => SUBSCRIPTION_LIMITATIONS[WORKSPACE_SUBSCRIPTION.TRIAL].projects, [isFreeTrial]);

  // mutations/queries
  const [getWorkspace] = useLazyQuery(GET_WORKSPACE, { fetchPolicy: 'network-only' });
  const [getWorkspaceProfile] = useLazyQuery(GET_WORKSPACE_PROFILE, { fetchPolicy: 'network-only' });
  const [getUserRoles] = useLazyQuery(GET_WORKSPACE_ROLES, { fetchPolicy: 'network-only' });
  const [getRoles] = useLazyQuery(GET_ROLES_INFO, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (workspaceRegex.test(window.location.host)) setHandle(window.location.host.split('.')[0]);
  }, []);

  useEffect(() => {
    const fetchWorkspaceByHandle = async () => {
      if (!isLoading) setIsLoading(true);
      const { data: workspaceData, error: workspaceError, loading: workspaceLoading } = await getWorkspace({ variables: { input: { handle } } });

      if (!workspaceError && !workspaceLoading && workspaceData) {
        setWorkspace(workspaceData.getWorkspace);
        sessionStorage.setItem(AUTHORITY, workspaceData.getWorkspace.id);
      }

      const [workspaceProfileRes, workspaceRoles, systemRoles] = await Promise.all([
        getWorkspaceProfile({ variables: { input: { userId: authUser.id, workspaceId: workspaceData.getWorkspace.id } } }),
        getUserRoles({ variables: { workspaceId: workspaceData.getWorkspace.id } }),
        getRoles(),
      ]);

      const { data: workspaceProfileData, error: profileError, loading: profileLoading } = workspaceProfileRes;

      if (!profileError && !profileLoading && workspaceProfileData) {
        setWorkspaceProfile(workspaceProfileData.getWorkspaceProfile);
      }

      const { data: { roles: allUserRoles }, loading: userRolesLoading, error: userRolesError } = systemRoles;

      if (!userRolesLoading && !userRolesError && allUserRoles) {
        const rolesWithValue = allUserRoles.roles.map((role) => ({ ...role, value: ROLES[role.id].value }));
        setUserRoles(rolesWithValue);
      }

      const { data: { getWorkspaceUserRoles: roles } = {}, error: roleError, loading: roleLoading } = workspaceRoles;

      if (!roleLoading && !roleError && roles && roles.length > 0) {
        const permissions = roles.map(({ rolePermission }) => rolePermission.permission.name);
        const roleId = parseInt(roles[1].rolePermission.roleId, 10);

        const fetchedData = {
          permissions,
          roleId,
          role: ROLES[roles[1].rolePermission.roleId].value,
        };

        setTimelineTour(fetchedData.displayTour);
        setTeamTour(fetchedData.displayTour);
        setEpicTour(fetchedData.displayTour);
        setProjectTour(fetchedData.displayTour);

        setUserPermissions(fetchedData);
      }
    };

    if (handle && authUser) fetchWorkspaceByHandle();
  }, [handle, authUser]);

  // constants
  const values = useMemo(() => ({
    workspace,
    isFreeTrial,
    freeUsersLimit,
    freeAddProjectsLimit,
    userRoles,
    workspaceProfile,
    timelineTour,
    teamTour,
    epicTour,
    projectTour,
    showUpgradePlan,
    setTimelineTour,
    setTeamTour,
    setEpicTour,
    setProjectTour,
    setWorkspace,
    userPermissions,
    setHandle,
    setWorkspaceProfile,
    setShowUpgradePlan,
  }), [workspace, showUpgradePlan, userRoles, workspaceProfile, timelineTour, teamTour, epicTour, projectTour]);

  return (
    <WorkspaceContext.Provider value={values}>
      {children}
    </WorkspaceContext.Provider>
  );
};

const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

WorkspaceProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default useWorkspace;
